import { Injectable, signal, inject } from '@angular/core';
import { interval, Subject } from 'rxjs';
import { LocalStorage, LocalStorageService } from 'ngx-webstorage';
import { get } from 'lodash-es';
import { USER_ROLES } from '../../consts/roles.enum';
import { IAuthLogin, IAuthToken, IPermissions, Permits } from '../../types/auth.type';
import { IProfile } from '../../types/profile.type';
import { NzModalService } from 'ng-zorro-antd/modal';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../../../pages/auth/services/auth.service';
import { Logger } from '../logger/logger.service';
import { ROUTE_AUTH, ROUTE_LOGIN } from '../../consts/routes.const';
import { findMainRole } from '../../helpers/role';
import { MESSAGE_ERROR_TOKEN_EXPIRED } from '../../consts/messages.const';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private modal = inject(NzModalService);
  private authService = inject(AuthService);
  private logger = inject(Logger);
  private storage = inject(LocalStorageService);
  private router = inject(Router);

  /**
   * Пользователь аутентифицирован
   */
  private _loggedIn = signal(false);
  public loggedIn = this._loggedIn.asReadonly();
  /**
   * Токен
   */
  @LocalStorage('accessToken')
  public accessToken!: string | null;
  /**
   * Рефреш
   */
  @LocalStorage('refreshToken')
  public refreshToken!: string | null;
  /**
   * Данные профиля
   */
  public profile?: IProfile;
  /**
   * Права доступа пользователя
   */
  private permits?: IPermissions;
  /**
   * Подписка на токен
   */
  private tokenExpired = new Subject<string | null>();
  public refreshing: boolean = false;

  constructor() {
    this._loggedIn.set(!!this.accessToken);

    this.storage.observe('accessToken').subscribe((accessToken) => {
      this._loggedIn.set(!!accessToken);
      if (!accessToken) {
        this.router.navigate([`/${ROUTE_AUTH}/${ROUTE_LOGIN}`]);
      }
    });
  }

  /**
   * Проверка на авторизован?
   */
  isLoggedIn() {
    return !!this.accessToken;
  }

  /**
   * Выйти
   */
  logout() {
    this.authService.logout().subscribe({
      next: () => {},
      error: (err) => {
        this.logger.error('Logout error', err);
      },
    });
    this.profile = undefined;
    this.permits = undefined;
    this.accessToken = '';
    this.refreshToken = '';
  }

  /**
   * Обновить токены
   * @param data - данные из аутентификации
   */
  setToken(data: IAuthLogin) {
    this.accessToken = data.accessToken;
    this.refreshToken = data.refreshToken;
    this.getUserInfo();
  }

  /**
   * Формируем информацию о профиле на основе токена
   */
  getUserInfo() {
    if (!this.accessToken) {
      this.profile = undefined;
      this.permits = undefined;
      return;
    }

    const token = this.decodeToken();
    if (token) {
      const mainRole = findMainRole(token.groups);

      this.profile = {
        id: token.sub,
        userName: token.username,
        fio: this.generateFio(token),
        role: mainRole,
        roleName: get(USER_ROLES, mainRole),
      };
      this.permits = token.permits || {};
    }
  }

  /**
   * Имя пользователя в формате: Фамилия И. О.
   */
  private generateFio(token: IAuthToken): string {
    return `${token.person.lastName || ''} ${token.person.firstName ? token.person.firstName.charAt(0) + '.' : ''} ${
      token.person.middleName ? token.person.middleName.charAt(0) + '.' : ''
    }`;
  }

  /**
   * Расшифровка JWT токена
   */
  decodeToken(): IAuthToken | undefined {
    const token = this.accessToken || '';
    if (!token) {
      return undefined;
    }

    try {
      return token ? jwtDecode(token) : undefined;
    } catch (e) {
      this.logger.log('failed decode jwt token', e);
      return undefined;
    }
  }

  /**
   * Проверка токена
   */
  async init() {
    if (!this.accessToken) return;

    this.getUserInfo();
    // fixme
    if (false /*environment.checkExpired*/) {
      interval(1000)
        .pipe(takeUntil(this.tokenExpired))
        .subscribe(() => {
          if (this.isTokenExpired()) {
            this.tokenExpired.next(null);
            this.tokenExpired.complete();
            this.modal.error({
              nzTitle: MESSAGE_ERROR_TOKEN_EXPIRED,
              nzClosable: false,
              nzOnOk: () => {
                window.location.reload();
              },
            });
          }
        });
    }
  }

  /**
   * Список ролей пользователя
   */
  public getRoles(): string[] {
    const token = this.decodeToken();
    return token?.groups || [];
  }

  hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }

  hasAnyPermit(permits: Permits[]): boolean {
    const userPermits = this.permits;
    if (!userPermits) return false;

    return permits.some((permit) => {
      const permitValue = userPermits[permit];
      return !!permitValue && permitValue.length > 0;
    });
  }

  //  **Метод для получения разрешений (permissions)**
  public getPermits(key: Permits): number[] {
    if (!this.permits) return [];
    return this.permits[key];
  }

  /**
   * Токен истек?
   */
  public isTokenExpired(offsetSeconds = 60): boolean {
    const d = this.getTokenExpirationDate();
    offsetSeconds = offsetSeconds || 0;
    if (d === null) {
      return true;
    }

    return !(d.valueOf() > new Date().valueOf() + offsetSeconds * 1000);
  }

  /**
   * Дата истечения токена
   * @private
   */
  private getTokenExpirationDate() {
    const token = this.decodeToken();

    if (!token?.exp) {
      return null;
    }

    const d = new Date(0);
    d.setUTCSeconds(token.exp);
    return d;
  }
}
